import { CircleCheck, X } from "lucide-react";
import { Controller, useFormContext, useWatch } from "react-hook-form";

import shared from "@/shared/components/invocationSpec/invocationSpec.module.css";
import { RobotoButton } from "@/shared/foundation/primitives";

import { type TriggerFormValues } from "../../triggerFormSchema";
import styles from "../targets.module.css";

/** Names the changed file in a target's parameters. */
const CHANGED_FILE_VARIABLE = "file.relative_path";

/** Names the session in a target's parameters. */
const SESSION_VARIABLE = "session.session_id";

export interface InputDataHeaderProps {
  index: number;
  /** What the change itself supplies, as `suppliedInputFor` names it. */
  supplied: "changed-file" | "changed-dataset" | "changed-session";
}

/**
 * What the firing already hands the action, at the top of "Input data".
 *
 * Nothing here is authored: the firing decides what it hands over, so the block
 * states it. What a stored trigger arrived with and the form has no control for
 * -- a file gate a condition cannot state, extra input patterns -- is shown as
 * it is and can be removed, so saving never drops it silently.
 */
export function InputDataHeader({ index, supplied }: InputDataHeaderProps) {
  const { control } = useFormContext<TriggerFormValues>();
  const stored = useWatch<TriggerFormValues>({
    control,
    name: `targets.${index}.storedAdditionalInputs`,
  }) as string[] | undefined;
  const requiredInputs = useWatch<TriggerFormValues>({
    control,
    name: `targets.${index}.requiredInputs`,
  }) as string[] | undefined;

  return (
    <>
      {supplied === "changed-file" ? (
        <div
          className={styles.suppliedAlert}
          role="note"
          data-testid="supplied-input"
          data-supplied={supplied}
        >
          <CircleCheck
            aria-hidden
            size={16}
            className={styles.suppliedAlertIcon}
          />
          <p className={styles.suppliedAlertText}>
            The file that changed is passed as input. In parameters, reference
            it as{" "}
            <code className={styles.variable}>
              {`{{${CHANGED_FILE_VARIABLE}}}`}
            </code>
            .
          </p>
        </div>
      ) : (
        <div
          className={styles.suppliedAlert}
          role="note"
          data-testid="supplied-input"
          data-supplied={supplied}
        >
          <CircleCheck
            aria-hidden
            size={16}
            className={styles.suppliedAlertIcon}
          />
          {supplied === "changed-session" ? (
            <p className={styles.suppliedAlertText}>
              The session is passed as input. In parameters, reference it as{" "}
              <code className={styles.variable}>
                {`{{${SESSION_VARIABLE}}}`}
              </code>
              .
            </p>
          ) : (
            <p className={styles.suppliedAlertText}>
              The invocation runs on the dataset that changed. Add an input
              below to mount files from it.
            </p>
          )}
        </div>
      )}

      {/* A gate a condition cannot state exactly -- a glob whose `*` stops at
          a `/`, or several the runtime reads as alternatives. It is shown and
          sent as it arrived rather than rewritten into a trigger that fires on
          more than it used to. */}
      {supplied === "changed-file" &&
        requiredInputs !== undefined &&
        requiredInputs.length > 0 && (
          <Controller
            control={control}
            name={`targets.${index}.requiredInputs`}
            render={({ field }) => (
              <div
                className={shared.readOnlyRow}
                data-testid="stored-file-gate"
                data-count={requiredInputs.length}
              >
                <span>Only files matching: {requiredInputs.join(", ")}</span>
                <RobotoButton
                  variant="ghost"
                  icon={<X aria-hidden size={16} />}
                  aria-label="Remove the file patterns"
                  onClick={() => {
                    field.onChange([]);
                  }}
                />
              </div>
            )}
          />
        )}

      {stored !== undefined && stored.length > 0 && (
        <Controller
          control={control}
          name={`targets.${index}.storedAdditionalInputs`}
          render={({ field }) => (
            <div
              className={shared.readOnlyRow}
              data-testid="stored-additional-inputs"
              data-count={stored.length}
            >
              <span>Extra file patterns: {stored.join(", ")}</span>
              <RobotoButton
                variant="ghost"
                icon={<X aria-hidden size={16} />}
                aria-label="Remove the extra file patterns"
                onClick={() => {
                  field.onChange([]);
                }}
              />
            </div>
          )}
        />
      )}
    </>
  );
}
